import {
  ApiAttributionModel,
  ApiChartType,
  ApiChartYAxisKey,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import GlassCard, { IGlassCardProps } from "Components/GlassCard/GlassCard";
import ReportBubbleTitle from "..//components/ReportBubbleTitle";
import { IChart, TChartData } from "Interfaces";
import {
  memo,
  ReactNode,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { useIsMobile } from "Hooks/useIsMobile";
import { useContainerQuery } from "react-container-query";
import { useUpdateEffect } from "react-use";
import clsx from "clsx";
import Loading from "Components/Loading/Loading";
import { IFromToChartOutput, useFromToContext } from "Providers/FromToProvider";
import { IOnClickMap, TTableCustomCellRender } from "..//types/types";
import { AnimatePresence, motion } from "motion/react";
import { FilterAlt, MoreHoriz } from "@mui/icons-material";
import CustomDialog from "Components/CustomDialog/CustomDialog";
import {
  filterNonTrendDimensions,
  filterTrendDimensions,
  getFirstMetricFromY,
  getMetricByIndex,
} from "../utils/utils";
import AnalyticsCardFiltersTooltip from "../components/AnalyticsCardFiltersTooltip";
import AnalyticsChart from "../components/AnalyticsChart";
import AnalyticsCardToolBar from "../components/AnalyticsCardToolBar";
import AnalyticsFilterDropdown from "../components/AnalyticsFilterDropdown";
import AnalyticsAttributionSidebar from "../components/AnalyticsAttributionSidebar";
import { useMetricName } from "features/analytics/hooks/useMetricName";
import { useDimensionName } from "features/analytics/hooks/useDimensionName";
import { useAttributionContext } from "Providers/AttributionProvider";
import ErrorBoundary from "features/common/components/ErrorBoundary";
import { getAiInsights } from "../services/analyticsService";
import { useSelectedProject } from "Hooks";
import Markdown from "react-markdown";
import { ReactComponent as AiIcon } from "Assets/icons/menu/ai.svg";

const StyledAlert = styled(Alert)(({ theme }) => ({
  "& .MuiAlert-message": {
    width: "100%",
  },
  "& p": {
    marginTop: 4,
    marginBottom: 4,
  },
  "& h1, & h2, & h3, & h4, & h5, & h6, & ul, & ol": {
    marginTop: 8,
    marginBottom: 8,
  },
}));

export interface IAnalyticsCardProps extends Omit<IGlassCardProps, "onClick"> {
  chart: IChart;
  /** When using displayOptions.availableDimensions, dimensions to keep at the start (not replaced by the dropdown). E.g. [ApiDimension.DIMENSION_INTERNAL_SPLIT_CAMPAIGN]. */
  staticDimensions?: ApiDimension[];
  onChangeMetric?: (m: ApiMetric) => void;
  onChangeDimension?: (m: ApiDimension) => void;
  metricsOverride?: ApiMetric[]; // used if we want to grab different metrics than specified in the selectedMetric
  customDataFn?: (arr: TChartData[]) => TChartData[]; // only working wirth tables at the moment
  defaultFirstFilter?: { [key in ApiDimension]?: string }; //used when using displayoptions.filterableDropdowns to pre select value
  overrideDate?: IFromToChartOutput;
  overrideComparisonDate?: IFromToChartOutput;
  onModelsChange?: (models: ApiAttributionModel[]) => void;
  onClick?: {
    [field: string]:
      | ((v: string, o?: string) => void | Promise<void>)
      | IOnClickMap[];
  };
  customRightTop?: React.ReactNode;
  customFullRight?: React.ReactNode;
  customHeader?: React.ReactNode;
  customTitleFn?: (d: ApiDimension, m: ApiMetric) => string; // params are selected dimension and metric
  leadId?: number;
  onDataChange?: (
    arr: TChartData[],
    comp?: TChartData[],
    meta?: { metric?: ApiMetric; totals?: TChartData },
  ) => void;
  onVisibleRowsChange?: (rows: any[]) => void;
  tableCustomCellRender?: TTableCustomCellRender;
  tableCustomHeaderRender?: TTableCustomCellRender;
  tableHiddenCols?: (ApiMetric | ApiDimension)[];
  tableHeaderAlign?: Partial<
    Record<ApiDimension | ApiMetric | string, "left" | "center">
  >;
  tableNonSortableCols?: (ApiMetric | ApiDimension | string)[];
  tableUnpinnedCols?: (ApiMetric | ApiDimension | string)[];
  onExport?: () => Promise<void>;
  onEdit?: () => Promise<void>;
  onDelete?: () => Promise<void>;
  noToolbar?: boolean;
  body?: ReactNode;
  ignoreAttrModel?: boolean;
  forceNoComparison?: boolean;
  funnelShowStageChange?: boolean;
  verticalReferenceLines?: Array<{
    x: string;
    label?: string | React.ReactElement;
    stroke?: string;
  }>;
  noTopBar?: boolean;
}

const ErrorFallback = ({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) => {
  return (
    <Box sx={{ p: 2, bgcolor: "error.light", borderRadius: 1 }}>
      <Alert severity="error" onClose={resetErrorBoundary}>
        {error.message}
      </Alert>
    </Box>
  );
};

const query = {
  sm: {
    minWidth: 0,
    maxWidth: 265,
  },
};

function AnalyticsCard({
  chart,
  staticDimensions: staticDimensionsProp,
  onChangeMetric,
  onChangeDimension,
  metricsOverride,
  customDataFn,
  defaultFirstFilter,
  overrideDate,
  overrideComparisonDate,
  onModelsChange,
  onClick,
  customRightTop,
  customFullRight,
  customHeader,
  customTitleFn,
  onDataChange,
  onVisibleRowsChange,
  tableCustomCellRender,
  tableCustomHeaderRender,
  tableHiddenCols,
  tableHeaderAlign,
  tableNonSortableCols,
  tableUnpinnedCols,
  boxProps,
  onExport,
  onEdit,
  onDelete,
  noToolbar,
  body,
  ignoreAttrModel,
  forceNoComparison,
  funnelShowStageChange,
  verticalReferenceLines,
  noTopBar,
  ...glass
}: IAnalyticsCardProps) {
  const { sx: glassSx, ...glassProps } = glass;
  const cardHeight =
    (glassSx as { height?: string | number } | undefined)?.height ?? "100%";
  const metricToName = useMetricName();
  const dimensionToName = useDimensionName();
  const { model } = useAttributionContext();
  const { selectedProject } = useSelectedProject();
  const [fullscreen, setFullscreen] = useState(false);
  const [filterOpen, setFFilterOpen] = useState(false);
  const [parsedChart, setParsedChart] = useState<IChart>(chart);
  const [filtersLoaded, setFiltersLoaded] = useState(
    (chart.displayOptions?.filterDropdowns || []).length === 0,
  ); // if we require filters then we need to wait for them
  const [params] = useContainerQuery(query, {});
  const [showLabel, setShowLabel] = useState(
    chart.displayOptions?.showLabels || false,
  );
  const [showToolBar, setShowToolBar] = useState(false);
  const [filterContainerWidth, setFilterContainerWidth] = useState<number>(0);
  const filterContainerRef = useRef<HTMLDivElement>(null);
  const [aiInsights, setAiInsights] = useState<string>("");
  const [aiInsightsOpen, setAiInsightsOpen] = useState(false);
  const [aiInsightsLoading, setAiInsightsLoading] = useState(false);
  const { chartOutput } = useFromToContext();
  const isMobile = useIsMobile();
  const [isHovered, setIsHovered] = useState(false);

  const onGetAiInsights = useCallback(async () => {
    if (!selectedProject) return;
    setAiInsightsLoading(true);
    if (aiInsights === "") {
      const datePayload = overrideDate || chartOutput;
      const insights = await getAiInsights(
        selectedProject.id!,
        chart,
        datePayload.from,
        datePayload.to,
        datePayload.lastNDays,
        model || undefined,
      );
      setAiInsights(insights || "");
    }

    setAiInsightsOpen((open) => !open);
    setAiInsightsLoading(false);
    if (!fullscreen) {
      setShowToolBar(false);
    }
  }, [
    chart,
    overrideDate,
    selectedProject,
    fullscreen,
    chartOutput,
    aiInsights,
    model,
  ]);

  const handleResize = useCallback(() => {
    if (filterContainerRef.current) {
      setFilterContainerWidth(filterContainerRef.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  const firstMetric = useMemo(
    () => getFirstMetricFromY(chart?.yAxisKeys) || ApiMetric.METRIC_NOT_SET,
    [chart?.yAxisKeys],
  );

  const [selectedMetric, setSelectedMetric] = useState<ApiMetric>(firstMetric);

  useUpdateEffect(() => {
    setSelectedMetric(firstMetric);
  }, [firstMetric]);

  const trendDimensions = useMemo(
    () => filterTrendDimensions(chart?.dimension as ApiDimension[]),
    [chart.dimension],
  );

  const [selectedDimension, setSelectedDimension] = useState<ApiDimension>(
    () => {
      const first = (() => {
        const dims = chart?.dimension as ApiDimension[] | undefined;
        if (!dims?.length) return undefined;
        const trend = filterTrendDimensions(dims);
        const staticSet = new Set([...trend, ...(staticDimensionsProp ?? [])]);
        const availDims = chart.displayOptions?.availableDimensions ?? [];
        if (availDims.length > 0) {
          const firstReplaced = dims.find((d) => !staticSet.has(d));
          if (firstReplaced !== undefined) return firstReplaced;
        }
        const filtered = filterNonTrendDimensions(dims);
        return filtered[0];
      })();
      return first ?? ApiDimension.DIMENSION_NOT_SET;
    },
  );

  const availableMetrics = useMemo(
    () => chart.displayOptions?.availableMetrics || [],
    [chart.displayOptions],
  );
  const availableDimensions = useMemo(
    () => chart.displayOptions?.availableDimensions || [],
    [chart.displayOptions],
  );
  const filterableDropdowns = useMemo(
    () => chart.displayOptions?.filterDropdowns || [],
    [chart.displayOptions],
  );

  const staticDimensions = useMemo(
    () => staticDimensionsProp ?? [],
    [staticDimensionsProp],
  );

  const firstNonTrendDimension = useMemo(() => {
    const dims = chart?.dimension as ApiDimension[] | undefined;
    if (!dims?.length) return undefined;
    const staticSet = new Set([...trendDimensions, ...staticDimensions]);
    if (availableDimensions.length > 0) {
      const firstReplaced = dims.find((d) => !staticSet.has(d));
      if (firstReplaced !== undefined) return firstReplaced;
    }
    const filtered = filterNonTrendDimensions(dims);
    return filtered[0];
  }, [
    chart?.dimension,
    trendDimensions,
    staticDimensions,
    availableDimensions.length,
  ]);

  useUpdateEffect(() => {
    setSelectedDimension(
      firstNonTrendDimension ?? ApiDimension.DIMENSION_NOT_SET,
    );
  }, [firstNonTrendDimension]);

  useEffect(() => {
    const hasAvailDims = availableDimensions.length > 0;
    const hasAvailMetrics =
      (chart.displayOptions?.availableMetrics || []).length > 0;
    const hasMultipleY = (chart.yAxisKeys || []).length > 1;

    let displayOptions = chart.displayOptions
      ? { ...chart.displayOptions, showLabels: showLabel }
      : showLabel
        ? { showLabels: true }
        : undefined;

    const staticSet = new Set([...trendDimensions, ...staticDimensions]);
    const dimension = hasAvailDims
      ? (() => {
          const dims = (chart.dimension || []) as ApiDimension[];
          const prefix = dims.filter((d) => staticSet.has(d));
          const remaining = dims.filter((d) => !staticSet.has(d));
          return [...prefix, selectedDimension, ...remaining.slice(1)];
        })()
      : chart.dimension;

    const yAxisKeys = hasAvailMetrics
      ? hasMultipleY
        ? [
            {
              key: "l",
              fields: metricsOverride || [selectedMetric],
              chart: (chart.yAxisKeys[0] as ApiChartYAxisKey).chart,
              stackId: (chart.yAxisKeys[0] as ApiChartYAxisKey).stackId,
            },
            ...(chart.yAxisKeys || []).slice(1),
          ]
        : [
            {
              key: "l",
              fields: metricsOverride || [selectedMetric],
              chart: (chart.yAxisKeys[0] as ApiChartYAxisKey).chart,
              stackId: (chart.yAxisKeys[0] as ApiChartYAxisKey).stackId,
            },
          ]
      : chart.yAxisKeys;

    setParsedChart({ ...chart, displayOptions, dimension, yAxisKeys });
  }, [
    chart,
    selectedMetric,
    selectedDimension,
    metricsOverride,
    showLabel,
    trendDimensions,
    staticDimensions,
    availableDimensions,
    model,
  ]);

  useUpdateEffect(() => {
    if (onChangeMetric) {
      onChangeMetric(selectedMetric);
    }
  }, [selectedMetric]);
  useUpdateEffect(() => {
    if (onChangeDimension) {
      onChangeDimension(selectedDimension);
    }
  }, [selectedDimension]);

  const numberOfDropdowns = useMemo(
    () =>
      availableMetrics.length +
      availableDimensions.length +
      filterableDropdowns.length,
    [availableMetrics, availableDimensions, filterableDropdowns],
  );

  const chartTitle = useMemo(
    () =>
      customTitleFn
        ? customTitleFn(selectedDimension, selectedMetric)
        : availableMetrics.length > 0 && chart.name?.includes(" by")
          ? `${chart.name} ${metricToName(selectedMetric)}`
          : availableDimensions.length > 0 && chart.name?.includes(" by")
            ? `${chart.name} ${dimensionToName(selectedDimension)}`
            : chart.name,
    [
      customTitleFn,
      chart.name,
      selectedDimension,
      selectedMetric,
      availableMetrics,
      availableDimensions,
      metricToName,
      dimensionToName,
    ],
  );

  useUpdateEffect(() => {
    if (!fullscreen) {
      setShowToolBar(false);
    }
  }, [fullscreen]);

  const fullAttributesList = chart.displayOptions?.fullAttributesList ?? [];
  const hasFullAttributes = fullAttributesList.length > 0;

  const hasDropdowns = numberOfDropdowns > 0;

  const manyFilters =
    availableDimensions.length +
      availableMetrics.length +
      filterableDropdowns.length >
    1;
  const isNarrowFilterArea =
    filterContainerWidth > 0 && filterContainerWidth < 300 && manyFilters;

  const renderBody = (white?: boolean) => {
    return (
      <>
        <Stack
          direction={"row"}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          sx={{
            height: "100%",
            width: "100%",
            position: "relative",
          }}
        >
          <GlassCard
            boxProps={
              boxProps || {
                px: 2,
                pt: 2.5,
                pb: chart.type === ApiChartType.TABLE ? 6 : 3,
              }
            }
            {...glassProps}
            sx={{
              overflow: "hidden",
              position: "relative",
              background: white ? "white" : undefined,
              ...glassSx,
              width: "100%",
              height: cardHeight,
            }}
          >
            <Stack
              direction={"row"}
              justifyContent="space-between"
              sx={{
                height: "100%",
                width: "100%",
                minHeight: fullscreen ? "50vh" : undefined,
              }}
            >
              <Stack
                spacing={parsedChart.displayOptions?.noLegend ? 2 : 0}
                flex={1}
                sx={{
                  width: "100%",
                  height: "100%",
                  minHeight: 0,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent={"space-between"}
                  spacing={2}
                  mb={1}
                  sx={{ flexShrink: 0 }}
                >
                  <Stack
                    spacing={2}
                    sx={{
                      width: "100%",
                      flex: hasDropdowns ? `0 1 50%` : 1,
                    }}
                    mb={0}
                  >
                    {customHeader ? (
                      <Box sx={{ flex: 1, width: "100%" }}>{customHeader}</Box>
                    ) : (
                      !chart.displayOptions?.noTitle && (
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ lineHeight: 1.2, textWrap: "pretty" }}
                          >
                            {chartTitle}
                          </Typography>
                          {chart.description && (
                            <Typography
                              variant="body2"
                              color={"secondary"}
                              sx={{ lineHeight: 1.2 }}
                              fontWeight={500}
                            >
                              {chart.description}
                            </Typography>
                          )}
                        </Box>
                      )
                    )}
                  </Stack>
                  {customRightTop}
                  {(hasFullAttributes || hasDropdowns) && (
                    <Stack
                      ref={filterContainerRef}
                      direction={isNarrowFilterArea ? "column" : "row"}
                      spacing={2}
                      mb={1}
                      justifyContent={"flex-end"}
                      sx={{
                        maxWidth: "50%",
                        minWidth: "40%",
                        flex: 1,
                      }}
                      className={clsx(params)}
                    >
                      {hasFullAttributes && (
                        <Button
                          onClick={() => setFFilterOpen(true)}
                          size="small"
                          startIcon={<FilterAlt />}
                        >
                          Add Filters
                        </Button>
                      )}
                      {availableDimensions.length > 0 && (
                        <TextField
                          select
                          fullWidth
                          size="small"
                          sx={{ maxWidth: "100%", minWidth: 150, width: 200 }}
                          value={selectedDimension}
                          label="change dimension"
                          onChange={(e) => {
                            setSelectedDimension(
                              e.target.value as ApiDimension,
                            );
                          }}
                        >
                          {(availableDimensions || []).map((dimension) => (
                            <MenuItem key={dimension} value={dimension}>
                              {dimensionToName(dimension)}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                      {availableMetrics.length > 0 && (
                        <TextField
                          select
                          fullWidth
                          size="small"
                          sx={{ maxWidth: "100%", minWidth: 150, width: 200 }}
                          value={selectedMetric}
                          label="change metric"
                          onChange={(e) => {
                            setSelectedMetric(e.target.value as ApiMetric);
                          }}
                        >
                          {(availableMetrics || []).map((metric) => (
                            <MenuItem key={metric} value={metric}>
                              {metricToName(metric)}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                      {filterableDropdowns.length > 0 &&
                        filterableDropdowns.map((f) => (
                          <AnalyticsFilterDropdown
                            key={f}
                            dimension={f}
                            setChart={setParsedChart}
                            defaultFirstFilter={defaultFirstFilter}
                            isReady={() => setFiltersLoaded(true)}
                          />
                        ))}
                    </Stack>
                  )}
                  {!isMobile &&
                    chart.attributes.length > 0 &&
                    !chart.displayOptions?.noFilters && (
                      <AnalyticsCardFiltersTooltip
                        title={`${chart.name} Attributes`}
                        filters={chart.attributes}
                      />
                    )}
                </Stack>

                {chart.type === ApiChartType.BUBBLE && !false && (
                  <ReportBubbleTitle
                    metric={getMetricByIndex(2, chart.yAxisKeys) || ""}
                    showLabel={showLabel}
                    setShowLabel={setShowLabel}
                  />
                )}

                {filtersLoaded ? (
                  <Box
                    sx={{
                      flex: "1 1 auto",
                      display: "flex",
                      position: "relative", // <-- important
                      minHeight: 0,
                      height: "100%",
                    }}
                  >
                    {/* Chart stays mounted so height never changes */}
                    <Box
                      aria-hidden={aiInsightsOpen && !fullscreen}
                      sx={{
                        flex: 1,
                        width: "100%",
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        overflowY:
                          isMobile && chart.type === ApiChartType.FUNNEL
                            ? "hidden"
                            : "visible",
                        overflowX:
                          isMobile && chart.type === ApiChartType.FUNNEL
                            ? "auto"
                            : "visible",
                        scrollSnapType: "x mandatory",
                        scrollBehavior: "smooth",
                        "&::-webkit-scrollbar": {
                          display: "none",
                        },
                      }}
                    >
                      {body ? (
                        <Box
                          sx={{
                            flex: 1,
                            width: "100%",
                            minHeight: 0,
                            minWidth: 0,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          {body}
                        </Box>
                      ) : (
                        <AnalyticsChart
                          chart={parsedChart}
                          customDataFn={customDataFn}
                          overrideDate={overrideDate}
                          overrideComparisonDate={overrideComparisonDate}
                          onClick={onClick}
                          onDataChange={onDataChange}
                          onVisibleRowsChange={onVisibleRowsChange}
                          tableCustomCellRender={tableCustomCellRender}
                          tableCustomHeaderRender={tableCustomHeaderRender}
                          tableHiddenCols={tableHiddenCols}
                          tableHeaderAlign={tableHeaderAlign}
                          tableNonSortableCols={tableNonSortableCols}
                          tableUnpinnedCols={tableUnpinnedCols}
                          tableSx={{ bottom: -47, top: noTopBar ? -30 : 10 }}
                          funnelShowStageChange={funnelShowStageChange}
                          verticalReferenceLines={verticalReferenceLines}
                          ignoreAttrModel={ignoreAttrModel}
                          forceNoComparison={forceNoComparison}
                        />
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Loading text="Loading Filters" />
                )}
                {aiInsights !== "" && !aiInsightsLoading && fullscreen && (
                  <StyledAlert
                    icon={<AiIcon width={20} height={20} />}
                    severity="info"
                    sx={{
                      maxHeight: 400,
                      height: "40%",
                      overflowY: "auto",
                      mt: chart.type === ApiChartType.TABLE ? 6 : 2,
                    }}
                    onClose={
                      !fullscreen ? () => setAiInsightsOpen(false) : undefined
                    }
                  >
                    <Markdown>{aiInsights}</Markdown>
                  </StyledAlert>
                )}
              </Stack>
              {customFullRight}
            </Stack>
          </GlassCard>
          {!noToolbar && (
            <AnimatePresence mode="wait">
              {showToolBar ? (
                <AnalyticsCardToolBar
                  setOpen={setShowToolBar}
                  fullscreen={fullscreen}
                  setFullscreen={setFullscreen}
                  chart={chart}
                  onExport={onExport}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onAiInsights={onGetAiInsights}
                  aiInsightsLoading={aiInsightsLoading}
                  aiInsightsLoaded={aiInsights !== ""}
                />
              ) : (
                isHovered && (
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <IconButton
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        right: 5,
                        padding: 0.5,
                      }}
                      onClick={() => setShowToolBar(true)}
                    >
                      <MoreHoriz sx={{ fontSize: 30 }} />
                    </IconButton>
                  </Box>
                )
              )}
            </AnimatePresence>
          )}
          {/* Insights overlay (only overlays when not fullscreen) */}
          <AnimatePresence>
            {aiInsightsOpen && !fullscreen && (
              <Box
                component={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                sx={{
                  position: "absolute",
                  inset: 0,
                  overflow: "auto",
                  display: "flex",
                  alignItems: "flex-start",
                  pointerEvents: "auto",
                  bgcolor: "transparent",
                  zIndex: 1,
                  borderRadius: "10px",
                }}
              >
                <StyledAlert
                  severity="info"
                  icon={<AiIcon width={20} height={20} />}
                  sx={{ width: "100%", minHeight: "100%", m: 0 }} // no margin to avoid pushing
                  onClose={() => setAiInsightsOpen(false)}
                >
                  {aiInsightsLoading ? (
                    <Loading text="Generating insights..." />
                  ) : (
                    <Markdown>{aiInsights}</Markdown>
                  )}
                </StyledAlert>
              </Box>
            )}
          </AnimatePresence>
        </Stack>
      </>
    );
  };

  if (fullscreen) {
    return (
      <CustomDialog
        open
        fullWidth
        allowBackdropClick
        maxWidth="lg"
        onClose={() => {
          setFullscreen(false);
        }}
      >
        <Box sx={{ width: "100%", height: "60vh" }}>{renderBody(true)}</Box>
      </CustomDialog>
    );
  }

  return (
    <ErrorBoundary
      fallback={({ error, resetErrorBoundary }) => (
        <ErrorFallback error={error} resetErrorBoundary={resetErrorBoundary} />
      )}
    >
      <Box sx={{ width: "100%", height: cardHeight }}>
        {renderBody()}
        {(chart.displayOptions?.fullAttributesList || []).length > 0 && (
          <AnalyticsAttributionSidebar
            open={filterOpen}
            setOpen={setFFilterOpen}
            chart={parsedChart}
            setChart={setParsedChart}
            dimensions={chart.displayOptions!.fullAttributesList!}
          />
        )}
      </Box>
    </ErrorBoundary>
  );
}

export default memo(AnalyticsCard);
